/* Sagestry: the opening scene of the home page (sections/sages-hero.liquid).
   Ported from design/mockups/draft/kit/hero.js: the haze intro, the name placed on the sun, and the way the
   valley sinks and hazes over as the visitor leaves it. Seasons and weather are not ported yet.

   Horizon scrolls inside .page-wrapper on wide screens, so nothing here reads scrollY: how far the scene has
   been left behind is read from its own rectangle. */

const d = document;
const root = d.documentElement;
const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
const editing = root.classList.contains('shopify-design-mode');
const clamp01 = (/** @type {number} */ x) => Math.max(0, Math.min(1, x));

/** @param {HTMLElement} hero */
function setup(hero) {
  if (hero.dataset.sgReady) return;
  hero.dataset.sgReady = '1';

  const media = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__media'));
  const mark = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__mark'));
  const sub = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__sub'));
  const veil = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__veil'));
  const line = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__line'));
  const curtain = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__curtain'));
  const haze = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__haze'));
  const rise = /** @type {HTMLElement | null} */ (hero.querySelector('.sg-hero__rise'));
  if (!media) return;

  /* where the sun is: the picture covers the scene, so its sun moves as the window changes shape */
  const sun = {
    x: (+(hero.dataset.sunX || 51) || 51) / 100,
    y: (+(hero.dataset.sunY || 39.5) || 39.5) / 100,
    r: (+(hero.dataset.sunR || 12.5) || 12.5) / 100,
  };
  const ratio = +(hero.dataset.ratio || 1.49) || 1.49;
  function place() {
    const W = hero.clientWidth;
    const H = hero.clientHeight;
    const w = Math.max(W, H * ratio);
    const h = w / ratio;
    hero.style.setProperty('--sg-sx', `${((W - w) / 2 + sun.x * w).toFixed(1)}px`);
    hero.style.setProperty('--sg-sy', `${((H - h) / 2 + sun.y * h).toFixed(1)}px`);
    hero.style.setProperty('--sg-sr', `${(sun.r * w).toFixed(1)}px`);
  }
  place();
  addEventListener('resize', place);

  /* the intro: the screen is haze for the first second, then the haze lifts off the valley from the bottom up.
     It plays to the end even if the visitor scrolls; Tab or Escape skips it. Once per visit. */
  function intro() {
    /** @type {Animation[]} */
    const all = [];
    /** @param {Element | null} el @param {Keyframe[]} frames @param {KeyframeAnimationOptions} o */
    const run = (el, frames, o) => {
      if (el) all.push(el.animate(frames, { ...o, fill: 'both' }));
    };
    root.classList.remove('sg-intro-wait');
    if (curtain) curtain.hidden = false;
    run(media, [{ transform: 'translate3d(0,26vh,0) scale(1.005)' }, { transform: 'none' }], {
      duration: 3400,
      easing: 'cubic-bezier(.26,.33,.29,1.04)',
    });
    run(veil, [{ opacity: 1 }, { opacity: 0.6, offset: 0.3 }, { opacity: 0 }], { duration: 900 });
    run(
      curtain,
      [
        { transform: 'translate3d(0,0,0)' },
        { transform: 'translate3d(0,-3vh,0)', offset: 0.24 },
        { transform: 'translate3d(0,-160vh,0)' },
      ],
      { duration: 3700, delay: 150, easing: 'cubic-bezier(.45,.05,.4,1)' }
    );
    run(haze, [{ opacity: 1 }, { opacity: 1, offset: 0.5 }, { opacity: 0 }], {
      duration: 3700,
      delay: 150,
      easing: 'ease-in',
    });
    run(
      line,
      [
        { opacity: 0, transform: 'translateY(12px)' },
        { opacity: 1, transform: 'none', offset: 0.2 },
        { opacity: 1, offset: 0.78 },
        { opacity: 0, transform: 'translateY(-6px)' },
      ],
      { duration: 3900, delay: 250, easing: 'ease-in-out' }
    );
    run(
      mark,
      [
        { opacity: 0, transform: 'translate(-50%,calc(-50% + 20px))' },
        { opacity: 1, transform: 'translate(-50%,-50%)' },
      ],
      { duration: 900, delay: 3300, easing: 'ease-in-out' }
    );
    run(
      sub,
      [
        { opacity: 0, transform: 'translate(-50%,14px)' },
        { opacity: 1, transform: 'translate(-50%,0)' },
      ],
      { duration: 900, delay: 3650, easing: 'ease-out' }
    );
    try {
      sessionStorage.setItem('sg-intro', '1');
    } catch (e) {
      /* private mode: the intro simply plays again next time */
    }
    Promise.all(all.map((a) => a.finished))
      .then(() => {
        if (curtain) curtain.hidden = true;
        // hand the finished state back to the stylesheet, so the scroll below can move these things
        for (const a of all) a.cancel();
      })
      .catch(() => {});
    /** @param {KeyboardEvent} e */
    const skip = (e) => {
      if (e.key !== 'Tab' && e.key !== 'Escape') return;
      for (const a of all) {
        try {
          a.finish();
        } catch (x) {
          /* already finished */
        }
      }
      removeEventListener('keydown', skip);
    };
    addEventListener('keydown', skip);
  }

  let seen = false;
  try {
    seen = sessionStorage.getItem('sg-intro') === '1';
  } catch (e) {
    /* treated as a first visit */
  }
  const replay = /[?&]replay/.test(location.search);
  if (hero.dataset.intro === 'true' && !calm && !editing && (!seen || replay)) intro();
  else root.classList.remove('sg-intro-wait');

  /* after the intro: the valley sinks a little as you leave it, the words fade, and the next section's haze rises */
  let ticking = false;
  function leave() {
    ticking = false;
    if (calm || editing) return;
    const r = hero.getBoundingClientRect();
    const H = r.height;
    const y = Math.max(0, -r.top);
    if (y > H) return;
    media.style.translate = `0 ${(y * 0.32).toFixed(1)}px`;
    if (sub) sub.style.opacity = String(Math.max(0, 1 - y / (H * 0.45)));
    if (rise) rise.style.opacity = Math.pow(clamp01((y / H - 0.08) / 0.7), 1.2).toFixed(3);
  }
  d.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(leave);
    },
    { passive: true, capture: true }
  );
  leave();
}

function init() {
  for (const hero of /** @type {HTMLElement[]} */ (Array.from(d.querySelectorAll('[data-sg-hero]')))) setup(hero);
  // tells the small script in sections/sages-hero.liquid that this file arrived
  root.dataset.sgHero = 'ready';
}

d.addEventListener('shopify:section:load', init);
init();
